import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  Loader2,
  Timer,
  CheckCircle2,
  Circle,
  Play,
  Trophy,
  RefreshCcw,
  History,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Mock Part 3 Mode — PDI-only.
 * Spin up a 30-min simulated Part 3 lesson:
 *   • Random Level 3+ scenario brief
 *   • 14-competency live checklist
 *   • Countdown timer (visual only — finishing manually is allowed)
 *   • On finish: grade A / B / C / Not yet + missing comps + top-3 next focus
 */
export default function MockPart3() {
  const nav = useNavigate();
  const [view, setView] = useState("home"); // home | active | result
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [session, setSession] = useState(null);
  const [scenario, setScenario] = useState(null);
  const [allComps, setAllComps] = useState([]);
  const [observed, setObserved] = useState(new Set());
  const [secondsLeft, setSecondsLeft] = useState(30 * 60);
  const tickRef = useRef(null);

  const loadHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/api/pdi-mode/mock-part3`, {
        withCredentials: true,
      });
      setHistory(data.items || []);
    } catch {
      /* silent */
    }
  };

  useEffect(() => {
    loadHistory();
  }, []);

  // Countdown
  useEffect(() => {
    if (view !== "active") return;
    tickRef.current = setInterval(() => {
      setSecondsLeft((s) => Math.max(0, s - 1));
    }, 1000);
    return () => clearInterval(tickRef.current);
  }, [view]);

  const startSession = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/api/pdi-mode/mock-part3/start`,
        {},
        { withCredentials: true }
      );
      setSession(data.session);
      setScenario(data.scenario);
      setAllComps(data.all_competencies || []);
      setObserved(new Set());
      // Use server duration if present, default 30
      setSecondsLeft((data.session?.duration_minutes || 30) * 60);
      setView("active");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start session");
    } finally {
      setLoading(false);
    }
  };

  const toggleComp = async (key) => {
    if (!session) return;
    const next = new Set(observed);
    const willObserve = !next.has(key);
    if (willObserve) next.add(key);
    else next.delete(key);
    setObserved(next);
    try {
      await axios.post(
        `${API}/api/pdi-mode/mock-part3/${session.id}/observe`,
        { competency_key: key, observed: willObserve },
        { withCredentials: true }
      );
    } catch (e) {
      // Roll back UI on error
      const rb = new Set(observed);
      setObserved(rb);
      toast.error(e.response?.data?.detail || "Couldn't update");
    }
  };

  const finishSession = async () => {
    if (!session) return;
    if (
      !window.confirm(
        "Finish the mock now? You'll get an evidence-based grade based on what you ticked."
      )
    )
      return;
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/api/pdi-mode/mock-part3/${session.id}/finish`,
        {},
        { withCredentials: true }
      );
      setSession(data.session);
      setView("result");
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't finish");
    } finally {
      setLoading(false);
    }
  };

  const resetHome = () => {
    setSession(null);
    setScenario(null);
    setObserved(new Set());
    setView("home");
    loadHistory();
  };

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-950 text-white pb-24">
      <header className="sticky top-0 z-10 bg-slate-900 border-b border-slate-800">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
          <button
            data-testid="mock-back"
            onClick={() => (view === "active" ? finishSession() : nav("/pdi-hub"))}
            className="p-2 rounded-xl hover:bg-slate-800"
          >
            <ArrowLeft className="w-5 h-5 text-slate-400" />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-extrabold uppercase tracking-wider text-violet-400">
              Mock Part 3
            </p>
            <p className="text-[11px] text-slate-400 truncate">
              {view === "active"
                ? scenario?.title
                : "Simulated Part 3 lesson · evidence-based grade"}
            </p>
          </div>
          {view === "active" && <CountdownPill seconds={secondsLeft} />}
        </div>
      </header>

      {view === "home" && (
        <HomeView
          loading={loading}
          history={history}
          onStart={startSession}
        />
      )}

      {view === "active" && (
        <ActiveView
          scenario={scenario}
          allComps={allComps}
          observed={observed}
          onToggle={toggleComp}
          onFinish={finishSession}
          loading={loading}
        />
      )}

      {view === "result" && session && (
        <ResultView session={session} onAgain={() => { resetHome(); startSession(); }} onHome={resetHome} />
      )}
    </div>
  );
}

function CountdownPill({ seconds }) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  const tone =
    seconds < 5 * 60
      ? "bg-rose-600 text-white"
      : seconds < 10 * 60
      ? "bg-amber-500 text-slate-900"
      : "bg-violet-600 text-white";
  return (
    <div
      data-testid="mock-timer"
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-extrabold ${tone}`}
    >
      <Timer className="w-3.5 h-3.5" />
      {String(m).padStart(2, "0")}:{String(s).padStart(2, "0")}
    </div>
  );
}

function HomeView({ loading, history, onStart }) {
  return (
    <div className="max-w-lg mx-auto px-4 py-4 space-y-4">
      <div className="rounded-2xl bg-gradient-to-br from-violet-700 via-fuchsia-700 to-slate-900 p-5">
        <p className="text-[10px] font-extrabold uppercase tracking-wider text-violet-200">
          Coaching simulation
        </p>
        <h1 className="text-xl font-extrabold mt-1">
          Run a mock Part 3 in 30 minutes
        </h1>
        <p className="text-[12px] text-white/80 mt-2 leading-snug">
          We'll pick a Level 3+ scenario, start a 30-minute timer, and give you
          the live 14-competency checklist. Tick each competency as it shows up
          in your delivery. Finish when ready for an evidence-based grade.
        </p>
        <button
          data-testid="mock-start-btn"
          onClick={onStart}
          disabled={loading}
          className="mt-4 w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-xl bg-white text-slate-900 font-extrabold text-sm hover:bg-slate-100 disabled:opacity-60"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Play className="w-4 h-4" />
          )}
          Start mock now
        </button>
      </div>

      <div className="rounded-2xl bg-slate-900 border border-slate-800 p-4">
        <div className="flex items-center gap-2 mb-2">
          <History className="w-4 h-4 text-slate-400" />
          <p className="text-[11px] font-extrabold uppercase tracking-wider text-slate-400">
            Recent mocks
          </p>
        </div>
        {history.length === 0 ? (
          <p className="text-[12px] text-slate-500">
            No sessions yet — your first mock will appear here.
          </p>
        ) : (
          <ul data-testid="mock-history-list" className="space-y-2">
            {history.map((h) => (
              <li
                key={h.id}
                data-testid={`mock-history-${h.id}`}
                className="rounded-xl bg-slate-800 border border-slate-700 p-3"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-[12px] font-extrabold truncate">
                      {h.scenario_title || "Scenario"}
                    </p>
                    <p className="text-[10px] text-slate-400 mt-0.5">
                      {new Date(h.started_at).toLocaleString()}
                    </p>
                  </div>
                  <GradePill grade={h.grade} score={h.score} />
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function ActiveView({ scenario, allComps, observed, onToggle, onFinish, loading }) {
  const grouped = useMemo(() => {
    const out = { 1: [], 2: [], 3: [], 4: [] };
    for (const c of allComps) {
      out[c.level || 1].push(c);
    }
    return out;
  }, [allComps]);

  return (
    <div className="max-w-lg mx-auto px-4 py-4 space-y-4">
      {scenario && (
        <div data-testid="mock-scenario-brief" className="rounded-2xl bg-slate-900 border border-slate-800 p-4">
          <p className="text-[10px] font-extrabold uppercase tracking-wider text-violet-400">
            Scenario brief
          </p>
          <h2 className="text-base font-extrabold mt-1">{scenario.title}</h2>
          {scenario.summary && (
            <p className="text-[12px] text-slate-300 mt-1 leading-snug">
              {scenario.summary}
            </p>
          )}
          {scenario.pdi_lesson_structure?.length > 0 && (
            <div className="mt-3">
              <p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 mb-1">
                Suggested structure
              </p>
              <ol className="list-decimal pl-4 space-y-0.5">
                {scenario.pdi_lesson_structure.map((step, i) => (
                  <li key={i} className="text-[11px] text-slate-300">
                    {step}
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      )}

      <div className="rounded-2xl bg-slate-900 border border-slate-800 p-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[11px] font-extrabold uppercase tracking-wider text-violet-400">
            Competency checklist
          </p>
          <span
            data-testid="mock-observed-count"
            className="text-[11px] font-extrabold text-slate-300"
          >
            {observed.size} / {allComps.length}
          </span>
        </div>
        {[1, 2, 3, 4].map((lvl) => (
          <div key={lvl} className="mb-3 last:mb-0">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
              Level {lvl}
            </p>
            <ul className="space-y-1">
              {grouped[lvl].map((c) => {
                const on = observed.has(c.key);
                return (
                  <li key={c.key}>
                    <button
                      data-testid={`mock-comp-${c.key}`}
                      onClick={() => onToggle(c.key)}
                      className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded-xl border transition ${
                        on
                          ? "bg-emerald-600/15 border-emerald-600 text-emerald-200"
                          : "bg-slate-800 border-slate-700 text-slate-300 hover:border-violet-500"
                      }`}
                    >
                      {on ? (
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                      ) : (
                        <Circle className="w-4 h-4 text-slate-500 shrink-0" />
                      )}
                      <span className="text-[12px] font-bold flex-1">
                        {c.name}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>

      <button
        data-testid="mock-finish-btn"
        onClick={onFinish}
        disabled={loading}
        className="w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"
      >
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <CheckCircle2 className="w-4 h-4" />
        )}
        Finish & get my grade
      </button>
    </div>
  );
}

function ResultView({ session, onAgain, onHome }) {
  const fb = session.feedback || {};
  return (
    <div className="max-w-lg mx-auto px-4 py-4 space-y-4">
      <div data-testid="mock-result-card" className={`rounded-2xl p-5 ${gradeBg(session.grade)}`}>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-white/15 flex items-center justify-center">
            <Trophy className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-white/80">
              Your grade
            </p>
            <h1 data-testid="mock-result-grade" className="text-3xl font-extrabold text-white">
              {session.grade || "—"}
            </h1>
            <p className="text-[11px] text-white/80 mt-0.5">
              {session.score ?? 0} / 14 competencies in evidence
            </p>
          </div>
        </div>
        {fb.summary && (
          <p className="mt-4 text-[12px] text-white/90 leading-snug">
            {fb.summary}
          </p>
        )}
      </div>

      {fb.next_focus?.length > 0 && (
        <div className="rounded-2xl bg-slate-900 border border-slate-800 p-4">
          <p className="text-[11px] font-extrabold uppercase tracking-wider text-violet-400 mb-2">
            Top 3 next focus areas
          </p>
          <ol className="list-decimal pl-4 space-y-1">
            {fb.next_focus.map((n, i) => (
              <li key={i} data-testid={`mock-focus-${i}`} className="text-[12px] text-slate-200 font-bold">
                {n}
              </li>
            ))}
          </ol>
        </div>
      )}

      {fb.missing_competencies?.length > 0 && (
        <div className="rounded-2xl bg-slate-900 border border-slate-800 p-4">
          <p className="text-[11px] font-extrabold uppercase tracking-wider text-slate-400 mb-2">
            Missing competencies ({fb.missing_competencies.length})
          </p>
          <ul className="space-y-1">
            {fb.missing_competencies.map((c) => (
              <li
                key={c.key}
                className="text-[11px] text-slate-300 flex items-center gap-2"
              >
                <Circle className="w-3 h-3 text-slate-500 shrink-0" />
                <span className="font-bold">{c.name}</span>
                <span className="text-[10px] text-slate-500">L{c.level}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex gap-2">
        <button
          data-testid="mock-result-again"
          onClick={onAgain}
          className="flex-1 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-500 text-white font-extrabold text-sm inline-flex items-center justify-center gap-1.5"
        >
          <RefreshCcw className="w-4 h-4" /> Run another
        </button>
        <button
          data-testid="mock-result-home"
          onClick={onHome}
          className="flex-1 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-extrabold text-sm border border-slate-700"
        >
          Back to mock home
        </button>
      </div>
    </div>
  );
}

function GradePill({ grade, score }) {
  if (!grade) {
    return (
      <span className="text-[10px] font-extrabold px-2 py-0.5 rounded-full bg-slate-700 text-slate-300">
        In progress
      </span>
    );
  }
  return (
    <span
      className={`text-[10px] font-extrabold px-2 py-0.5 rounded-full ${gradePillBg(
        grade
      )}`}
    >
      {grade} · {score}/14
    </span>
  );
}

function gradeBg(grade) {
  if (grade === "A") return "bg-gradient-to-br from-emerald-600 to-emerald-800";
  if (grade === "B") return "bg-gradient-to-br from-violet-600 to-violet-800";
  if (grade === "C") return "bg-gradient-to-br from-amber-600 to-amber-800";
  return "bg-gradient-to-br from-slate-700 to-slate-900";
}

function gradePillBg(grade) {
  if (grade === "A") return "bg-emerald-600 text-white";
  if (grade === "B") return "bg-violet-600 text-white";
  if (grade === "C") return "bg-amber-500 text-slate-900";
  return "bg-slate-600 text-white";
}
